import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import {
  LayoutDashboard, BookOpen, TrendingUp, ClipboardCheck, ListChecks, Award, Mail, CalendarDays,
  Newspaper, PlayCircle, ExternalLink, LogOut, GraduationCap, ArrowLeft, Clapperboard,
} from 'lucide-react';

const NAV = [
  { to: '/eleve/dashboard', label: 'Tableau de bord', icon: LayoutDashboard, exact: true },
  { to: '/cours/eleve/voiture', label: 'Mes cours', icon: BookOpen },
  { to: '/cours/progression', label: 'Ma progression', icon: TrendingUp },
  { to: '/livret', label: 'Mon livret', icon: ClipboardCheck },
  { to: '/cours/eleve/bilans', label: 'Mes bilans', icon: ListChecks },
  { to: '/cours/eleve/examen-blanc', label: 'Permis blanc', icon: Award },
  { to: '/eleve/mes-shorts', label: 'Mes shorts', icon: Clapperboard },
  { to: '/mon-planning', label: 'Mon planning', icon: CalendarDays },
  { to: '/messages', label: 'Messages', icon: Mail, badge: true },
];

export default function EleveShell({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = () => api.messagesThreads().then((d) => { if (alive) setUnread(d?.total_unread || 0); }).catch(() => {});
    load();
    const iv = setInterval(load, 30000);
    return () => { alive = false; clearInterval(iv); };
  }, [pathname]);

  const onLogout = () => { logout(); navigate('/'); };

  return (
    <div className="App min-h-screen bg-[#0a0a0b] text-white flex flex-col md:flex-row" data-testid="eleve-shell">
      {/* Sidebar — desktop */}
      <aside className="hidden md:flex md:w-60 md:min-h-screen shrink-0 border-r border-white/10 bg-[#0d0d0e] flex-col">
        <div className="flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-900/40">
            <GraduationCap size={16} />
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">codevoiture.fr</div>
            <div className="text-white text-[14px] font-semibold leading-tight">Mon apprentissage</div>
          </div>
        </div>
        <nav className="flex flex-col gap-1 p-3 flex-1">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`elv-side-${n.label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
                className={`flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold whitespace-nowrap transition ${
                  active ? 'bg-red-600 text-white shadow-md shadow-red-900/40' : 'text-white/65 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={15} className="shrink-0" />
                <span className="flex-1">{n.label}</span>
                {n.badge && unread > 0 && (
                  <span data-testid="elv-side-unread-badge" className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10.5px] font-bold inline-flex items-center justify-center ${active ? 'bg-white text-red-600' : 'bg-red-600 text-white'}`}>{unread}</span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/10 space-y-1">
          <Link to="/cours/eleve/guide-aac" className="flex items-center gap-2.5 px-3.5 h-9 rounded-xl text-[12.5px] font-medium text-white/55 hover:bg-white/5 hover:text-white transition">
            <BookOpen size={13} /> Guide AAC
          </Link>
          <Link to="/tutos" className="flex items-center gap-2.5 px-3.5 h-9 rounded-xl text-[12.5px] font-medium text-white/55 hover:bg-white/5 hover:text-white transition">
            <PlayCircle size={13} /> Tutos vidéo
          </Link>
          <Link to="/magazine" className="flex items-center gap-2.5 px-3.5 h-9 rounded-xl text-[12.5px] font-medium text-white/55 hover:bg-white/5 hover:text-white transition">
            <Newspaper size={13} /> Magazine
          </Link>
          <Link to="/contact" className="flex items-center gap-2.5 px-3.5 h-9 rounded-xl text-[12.5px] font-medium text-white/55 hover:bg-white/5 hover:text-white transition">
            <Mail size={13} /> Nous contacter
          </Link>
          <Link to="/?public=1" data-testid="elv-back-to-site" className="flex items-center gap-2.5 px-3.5 h-9 rounded-xl text-[12.5px] font-medium text-white/55 hover:bg-white/5 hover:text-white transition">
            <ExternalLink size={13} /> Voir le site public
          </Link>
          <button onClick={onLogout} data-testid="elv-logout" className="w-full flex items-center gap-2.5 px-3.5 h-9 rounded-xl text-[12.5px] font-medium text-white/55 hover:bg-red-950/40 hover:text-red-300 transition">
            <LogOut size={13} /> Déconnexion
          </button>
          {user && <div className="px-3.5 pt-2 pb-1 text-white/35 text-[11px] font-mono-spec truncate">{user.email}</div>}
        </div>
      </aside>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        <header className="h-12 lg:h-14 border-b border-white/10 bg-[#0d0d0e]/60 backdrop-blur flex items-center justify-between px-4 lg:px-8 sticky top-0 z-40">
          <div className="flex items-center gap-2 min-w-0">
            {pathname !== '/eleve/dashboard' && (
              <button
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/eleve/dashboard'))}
                data-testid="elv-mobile-back"
                aria-label="Retour"
                className="md:hidden shrink-0 -ml-1 w-9 h-9 rounded-lg border border-white/10 bg-white/5 text-white/80 active:scale-95 inline-flex items-center justify-center"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="text-white/45 text-[11px] lg:text-[12px] font-mono-spec uppercase tracking-[0.2em] truncate">Espace élève</div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/messages" className="relative text-white/60 hover:text-white" data-testid="elv-top-mail">
              <Mail size={17} />
              {unread > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[15px] h-[15px] px-0.5 rounded-full bg-red-600 text-white text-[9px] font-bold inline-flex items-center justify-center">{unread}</span>}
            </Link>
            <span className="hidden sm:block text-white/50 text-[12px] truncate max-w-[160px]">{user?.name}</span>
            <button onClick={onLogout} className="md:hidden text-red-300 text-[12px] inline-flex items-center gap-1"><LogOut size={12} /> Quitter</button>
          </div>
        </header>
        <main className="pb-10">{children}</main>
      </div>
    </div>
  );
}
